import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../context/app.context";
import orderOptimizerUserPermissionsHelper from "./common/user-permissions/order-optimizer-user-permissions.helper";
import OrderOptimizerViewModel from "./common/types/order-optimizer-view-model";
import OrderOptimizerRailyListingComponent from "./raily/listing/order-optimizer-raily-listing.component";
import OrderOptimizerTaxiListingComponent from "./taxi/listing/order-optimizer-taxi-listing.component";
import OrderOptimizerCargoListingComponent from "./cargo/listing/order-optimizer-cargo-listing.component";

const OrderOptimizerListingComponent: FC = () => {
  const { user, selectedAppLanguage } = useAppContext();

  const userPermissions = useMemo(
    () => orderOptimizerUserPermissionsHelper.getPermissions(user?.roles!),
    []
  );

  const [selectedViewMode, setSelectedViewMode] =
    useState<OrderOptimizerViewModel | null>(null);

  const viewOptions = useMemo(
    () => [
      {
        mode: OrderOptimizerViewModel.RAILY,
        component: <OrderOptimizerRailyListingComponent />,
        hasPermission: userPermissions.hasAccessToRailyView,
      },
      {
        mode: OrderOptimizerViewModel.CARGO,
        component: <OrderOptimizerCargoListingComponent />,
        hasPermission: userPermissions.hasAccessToCargoView,
      },
      {
        mode: OrderOptimizerViewModel.TAXI,
        component: <OrderOptimizerTaxiListingComponent />,
        hasPermission: userPermissions.hasAccessToTaxiView,
      },
    ],
    [userPermissions]
  );

  const getPossibleViewOptions = useCallback(() => {
    return viewOptions.filter((option) => option.hasPermission);
  }, [viewOptions]);

  const possibleViewOptions = useMemo(
    () => getPossibleViewOptions(),
    [getPossibleViewOptions]
  );

  useEffect(() => {
    if (possibleViewOptions.length === 0) {
      return;
    }
    setSelectedViewMode(possibleViewOptions[0].mode);
  }, [possibleViewOptions]);

  const SelectedViewComponent = useMemo(
    () =>
      viewOptions.find((option) => option.mode === selectedViewMode)?.component,
    [selectedViewMode, viewOptions]
  );

  if (!SelectedViewComponent) {
    return null;
  }

  return SelectedViewComponent;
};

export default OrderOptimizerListingComponent;
